/**
 * Toaster — corner stack that subscribes to `useToast()`. Drop one near the
 * AppShell root; components everywhere call `pushToast(...)` to dispatch.
 *
 * Desktop: top-right. Mobile: bottom-centre.
 * Auto-dismiss + manual close. Animated in/out via framer-motion.
 */

import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  AlertTriangle,
  CheckCircle2,
  Info,
  X,
  XCircle,
  type LucideIcon,
} from "lucide-react";

import { Link } from "react-router-dom";

import { useToast, type ToastItem, type ToastVariant } from "@/hooks/useToast";

const VARIANT_META: Record<ToastVariant, { color: string; icon: LucideIcon }> = {
  info: { color: "var(--color-info)", icon: Info },
  success: { color: "var(--color-success)", icon: CheckCircle2 },
  warning: { color: "var(--color-warning)", icon: AlertTriangle },
  danger: { color: "var(--color-danger)", icon: XCircle },
};

export function Toaster() {
  const { toasts, dismiss } = useToast();
  const reduce = useReducedMotion();

  return (
    <div
      aria-live="polite"
      role="region"
      style={{
        position: "fixed",
        zIndex: "var(--z-toast)" as unknown as number,
        pointerEvents: "none",
        top: "max(env(safe-area-inset-top), 16px)",
        right: "max(env(safe-area-inset-right), 16px)",
        display: "flex",
        flexDirection: "column",
        gap: 12,
        maxWidth: 380,
        width: "calc(100vw - 32px)",
      }}
    >
      <AnimatePresence initial={false}>
        {toasts.map((t) => (
          <ToastRow key={t.id} item={t} onClose={() => dismiss(t.id)} reduce={reduce ?? false} />
        ))}
      </AnimatePresence>
    </div>
  );
}

function ToastRow({
  item,
  onClose,
  reduce,
}: {
  item: ToastItem;
  onClose: () => void;
  reduce: boolean;
}) {
  const meta = VARIANT_META[item.variant];
  const Icon = meta.icon;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: reduce ? 0 : 24 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: reduce ? 0 : 24 }}
      transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
      style={{
        pointerEvents: "auto",
        background: "var(--color-surface)",
        borderRadius: "var(--radius-md)",
        boxShadow: "var(--shadow-md)",
        padding: "12px 14px",
        borderLeft: `4px solid ${meta.color}`,
        display: "grid",
        gridTemplateColumns: "auto 1fr auto",
        gap: 12,
        alignItems: "start",
      }}
    >
      <Icon size={18} strokeWidth={1.75} color={meta.color} />
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            font: "500 14px/1.3 var(--font-sans)",
            color: "var(--color-primary)",
          }}
        >
          {item.title}
        </div>
        {item.body ? (
          <div
            style={{
              marginTop: 4,
              font: "400 13px/1.4 var(--font-sans)",
              color: "var(--color-muted)",
            }}
          >
            {item.body}
          </div>
        ) : null}
        {item.link ? (
          <Link
            to={item.link.to}
            onClick={onClose}
            style={{
              display: "inline-block",
              marginTop: 6,
              font: "600 13px/1.4 var(--font-sans)",
              color: "var(--color-link)",
            }}
          >
            {item.link.label}
          </Link>
        ) : null}
      </div>
      <button
        type="button"
        aria-label="Dismiss notification"
        onClick={onClose}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 24,
          height: 24,
          borderRadius: "var(--radius-pill)",
          color: "var(--color-muted)",
        }}
      >
        <X size={14} strokeWidth={1.75} />
      </button>
    </motion.div>
  );
}
